import type { ChessColor } from '@bn/shared';
import { FILES, PIECE_GLYPH, RANKS, squareName, type SquarePiece } from './helpers';

interface Props {
  squares: Array<SquarePiece | null>;
  /** Camp du joueur : l'échiquier est retourné pour les noirs. */
  orientation: ChessColor;
  selected: string | null;
  /** Cases atteignables depuis la case sélectionnée. */
  targets: Set<string>;
  lastMove: { from: string; to: string } | null;
  /** Case du roi en échec, mise en évidence. */
  checkSquare: string | null;
  interactive: boolean;
  onSquareClick: (square: string) => void;
}

export function ChessBoard({
  squares,
  orientation,
  selected,
  targets,
  lastMove,
  checkSquare,
  interactive,
  onSquareClick,
}: Props) {
  const rows = orientation === 'w' ? [0, 1, 2, 3, 4, 5, 6, 7] : [7, 6, 5, 4, 3, 2, 1, 0];
  const cols = orientation === 'w' ? [0, 1, 2, 3, 4, 5, 6, 7] : [7, 6, 5, 4, 3, 2, 1, 0];

  return (
    <div className="mx-auto w-full max-w-xl select-none">
      <div className="grid grid-cols-8 overflow-hidden rounded-lg shadow-lg shadow-slate-950/50 ring-1 ring-slate-700">
        {rows.map((row) =>
          cols.map((col) => {
            const name = squareName(row, col);
            const piece = squares[row * 8 + col] ?? null;
            const dark = (row + col) % 2 === 1;
            const isTarget = targets.has(name);
            const isSelected = selected === name;
            const isLast = lastMove?.from === name || lastMove?.to === name;

            return (
              <button
                key={name}
                type="button"
                onClick={() => onSquareClick(name)}
                disabled={!interactive}
                aria-label={`${name}${piece ? ` — ${PIECE_GLYPH[piece.type]}` : ''}`}
                className={`cellule relative flex items-center justify-center text-[7vw] leading-none transition sm:text-4xl ${
                  dark ? 'bg-[#8f6f4e]' : 'bg-[#e6d3b3]'
                } ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
              >
                {isLast && <span className="absolute inset-0 bg-amber-300/35" />}
                {checkSquare === name && (
                  <span className="absolute inset-0 bg-red-500/55" />
                )}
                {isSelected && (
                  <span className="absolute inset-0 ring-4 ring-inset ring-sky-400" />
                )}

                {piece && (
                  <span
                    className={`relative ${
                      piece.color === 'w'
                        ? 'text-white [text-shadow:0_0_2px_#000,0_1px_2px_rgba(0,0,0,.6)]'
                        : 'text-slate-900 [text-shadow:0_0_1px_rgba(255,255,255,.35)]'
                    }`}
                  >
                    {PIECE_GLYPH[piece.type]}
                  </span>
                )}

                {isTarget && (
                  <span
                    className={
                      piece
                        ? 'absolute inset-1 rounded-full ring-4 ring-sky-500/70'
                        : 'absolute size-1/4 rounded-full bg-sky-500/70'
                    }
                  />
                )}

                {/* Repères de colonnes et de rangées, dans les coins. */}
                {col === cols[0] && (
                  <span className="absolute left-0.5 top-0.5 text-[0.5rem] font-bold text-slate-900/50">
                    {RANKS[row]}
                  </span>
                )}
                {row === rows[7] && (
                  <span className="absolute bottom-0.5 right-0.5 text-[0.5rem] font-bold text-slate-900/50">
                    {FILES[col]}
                  </span>
                )}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}
